import { CustomerAddress } from '../models/customer-address.model';
import { InternationalAddress } from '../models/international-address.model';

/**
 * The GLOBAL duplicate-address guard: every surface that creates a customer address
 * (edit-address dialog, order dialog inline add, edit-shipment inline add, UPS builder
 * inline add) runs the entered address through this before saving, and warns with
 * "use existing / save anyway" on a hit. Exists because users clone an address just to
 * change who the attention contact is — which the address's `contactId` link solves.
 *
 * Looser than `normalizeAddressKey`: street suffixes are canonicalized ("151 Valpro
 * Drive" matches "151 Valpro Dr"), line2 is ignored (a suite split is the same
 * building), and small typos in the street name don't break the match as long as the
 * street number agrees. A hit is a similar street line corroborated by EITHER the
 * zip5 OR the city — so a mistyped zip digit on an otherwise identical address still
 * warns. Country is ignored for the same reason `normalizeAddressKey` ignores it.
 */
export function findSimilarCustomerAddress(
    entered: InternationalAddress,
    candidates: CustomerAddress[],
    excludeId = '',
): CustomerAddress | null {
    const line1 = similarLine1Key(entered.line1);
    if (!line1) return null;
    const zip = zip5(entered.postalCode);
    const city = normalizeToken(entered.city);
    return candidates.find(c => {
        if (!c || c.id === excludeId) return false;
        if (!line1Similar(line1, similarLine1Key(c.address?.line1))) return false;
        const cZip = zip5(c.address?.postalCode);
        if (zip && cZip && zip === cZip) return true;
        return !!city && normalizeToken(c.address?.city) === city;
    }) ?? null;
}

// Street lines match when their keys are equal, or when the first numeric token (house
// or box number) is identical and the space-stripped keys are within 2 edits — so
// "151 Valpro Driv" matches "151 Valpro Dr" and "PO Box 123" matches "P.O. Box 123",
// but "151" never matches "157" (different building).
function line1Similar(a: string, b: string): boolean {
    if (!a || !b) return false;
    if (a === b) return true;
    if (firstNumber(a) !== firstNumber(b)) return false;
    return editDistance(a.replace(/ /g, ''), b.replace(/ /g, '')) <= 2;
}

function firstNumber(s: string): string {
    return s.split(' ').find(t => /^\d+$/.test(t)) ?? '';
}

function editDistance(a: string, b: string): number {
    if (Math.abs(a.length - b.length) > 2) return 3;
    let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
    for (let i = 1; i <= a.length; i++) {
        const curr = [i];
        for (let j = 1; j <= b.length; j++) {
            curr[j] = a[i - 1] === b[j - 1]
                ? prev[j - 1]
                : 1 + Math.min(prev[j - 1], prev[j], curr[j - 1]);
        }
        prev = curr;
    }
    return prev[b.length];
}

const STREET_SUFFIXES: Record<string, string> = {
    street: 'st', drive: 'dr', road: 'rd', avenue: 'ave', lane: 'ln',
    boulevard: 'blvd', court: 'ct', circle: 'cir', highway: 'hwy', parkway: 'pkwy',
    place: 'pl', terrace: 'ter', trail: 'trl', crossing: 'xing', square: 'sq',
    north: 'n', south: 's', east: 'e', west: 'w',
};

function similarLine1Key(s?: string): string {
    return (s ?? '')
        .toLowerCase()
        .replace(/[.,#]/g, ' ')
        .split(/\s+/)
        .filter(Boolean)
        .map(word => STREET_SUFFIXES[word] ?? word)
        .join(' ');
}

function normalizeToken(s?: string): string {
    return (s ?? '').toLowerCase().replace(/\s+/g, ' ').trim();
}

function zip5(s?: string): string {
    return (s ?? '').replace(/\D/g, '').slice(0, 5);
}
